"use client";

import React, { useMemo } from "react";
import { Worker, WorkerCard } from "@/app/components/workercard/WorkerCard";
import { SkeletonCard } from "./SkeletonCard";
import { useOrdersContext } from "@/context/OrdersContext";
import { useSearchParams } from "next/navigation";

interface WorkerMyTeamSectionProps {
  activeTab: "my-team" | "marketplace" | undefined;
  selectedWorker: Worker | null;
  handleWorkerClick: (
    worker: Worker,
    source: "my-team" | "marketplace",
  ) => void;
  // New props required for internal logic
  searchQuery: string;
  suppressLoading?: boolean;
  forceLoading?: boolean;
}

export const WorkerMyTeamSection: React.FC<WorkerMyTeamSectionProps> = ({
  activeTab,
  selectedWorker,
  handleWorkerClick,
  searchQuery,
  suppressLoading = false,
  forceLoading = false,
}) => {
  const searchParams = useSearchParams();
  const currentWorkspaceId = searchParams?.get("workspaceId") || undefined;
  const {
    loadingOrders,
    workersFromOrders,
    loadingOrderWorkers,
    isInitialLoading,
    orders, // Need orders for length check to prevent flash
  } = useOrdersContext();

  // Show skeleton if:
  // 1. We are loading AND have no workers yet
  // 2. We have orders (data exists) but workers aren't processed yet (prevent flash of empty state)
  const showMyTeamLoading =
    !suppressLoading &&
    (forceLoading || isInitialLoading || loadingOrderWorkers);

  const purchasedServiceKeys = useMemo(() => {
    return new Set(
      orders
        .filter((order) => {
          if (!order.serviceId) return false;
          if (!currentWorkspaceId) return true;
          return (
            (order.workspaceId || order.workspace?.id) === currentWorkspaceId
          );
        })
        .map(
          (order) =>
            `${order.serviceId}:${order.workspaceId || order.workspace?.id || ""}`,
        ),
    );
  }, [orders, currentWorkspaceId]);

  // Show only workers backed by the /orders API for the active workspace.
  const purchasedWorkersFromOrders = useMemo(() => {
    return workersFromOrders.filter((worker) => {
      if (!worker.id) return false;
      if (!currentWorkspaceId) {
        return orders.some((order) => order.serviceId === worker.id);
      }
      return purchasedServiceKeys.has(
        `${worker.id}:${worker.workspaceId || currentWorkspaceId}`,
      );
    });
  }, [workersFromOrders, orders, currentWorkspaceId, purchasedServiceKeys]);

  // Filter My Team workers by search query
  const filteredMyTeamWorkers = useMemo(() => {
    if (!searchQuery.trim()) return purchasedWorkersFromOrders;

    const query = searchQuery.toLowerCase();
    return purchasedWorkersFromOrders.filter(
      (w) =>
        w.name.toLowerCase().includes(query) ||
        w.role.toLowerCase().includes(query) ||
        w.description.toLowerCase().includes(query) ||
        (w.skills &&
          w.skills.some((skill) => skill.toLowerCase().includes(query))),
    );
  }, [purchasedWorkersFromOrders, searchQuery]);

  return (
    <div className="animate-in slide-in-from-left-2 duration-300 mb-0">
      <div className="flex items-center justify-between px-1 transition-all duration-300 overflow-hidden opacity-100 max-h-10 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-aera-gray uppercase tracking-widest">
            ทีม AI ของร้าน
          </span>
          {filteredMyTeamWorkers.length > 0 && !showMyTeamLoading && (
            <span className="bg-aera-warmcream text-aera-amberdark text-[10px] font-bold px-1.5 py-0.5 rounded-md">
              {filteredMyTeamWorkers.length}
            </span>
          )}
        </div>
      </div>
      <div className="space-y-1">
        {showMyTeamLoading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <SkeletonCard key={i} isHired={true} />
          ))
        ) : (
          <>
            {filteredMyTeamWorkers.map((worker, index) => (
              <WorkerCard
                key={`${worker.id}-${index}`}
                worker={worker}
                isSelected={
                  activeTab === "my-team" &&
                  selectedWorker?.orderId === worker.orderId
                }
                isHired={true}
                onClick={(w) => handleWorkerClick(w, "my-team")}
              />
            ))}
            {filteredMyTeamWorkers.length === 0 &&
              !loadingOrders &&
              !loadingOrderWorkers &&
              !isInitialLoading && (
                <div className="text-center py-6 text-xs text-aera-gray">
                  ยังไม่มีบริการ
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
};
